import { IFacilityInfo } from '../FacilityTable/FacilityItem/FacilityItem'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { IRobotInfo } from 'utils/interfaces'

import style from './AddFacilityModal.module.css'

const FACILITY_TYPE_OPTIONS: IOption[] = [
  { value: 'commercial', text: 'Commercial & Institutional' },
  { value: 'educational', text: 'Educational' },
  { value: 'healthcare', text: 'Healthcare' },
  { value: 'industrial', text: 'Industrial / Manufacturing' },
  { value: 'public', text: 'Public / Government' },
  { value: 'hospitality', text: 'Hotels & Hospitality' },
  { value: 'residential', text: 'Residential' },
  { value: 'financial', text: 'Financial' },
  { value: 'parking', text: 'Parking' },
  { value: 'warehouse', text: 'Warehouse' },
  { value: 'other', text: 'Other' },
]

interface IAddFacilityModal {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    name: string
    facility_type: string
    street_address?: string
    city?: string
    country?: string
    robots_ids?: number[]
  }) => void
  className?: string
  robots: IRobotInfo[]
  facilityExisting?: IFacilityInfo
}

const AddFacilityModal: React.FC<IAddFacilityModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('New Facility')
  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [facilityType, setFacilityType] = useState('other')
  const [facilityTypeOption, setFacilityTypeOption] = useState<IOption>(
    FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1]
  )
  const [selectedRobotIds, setSelectedRobotIds] = useState<number[]>([])

  useEffect(() => {
    if (props.isOpen) {
      if (props.facilityExisting) {
        setName(props.facilityExisting.name)
        setStreetAddress(props.facilityExisting.street_address || '')
        setCity(props.facilityExisting.city || '')
        setCountry(props.facilityExisting.country || '')
        setFacilityType(props.facilityExisting.facility_type || 'other')
        setFacilityTypeOption(
          FACILITY_TYPE_OPTIONS.find(o => o.value === props.facilityExisting?.facility_type) ||
            FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1]
        )
        setSelectedRobotIds(props.facilityExisting.robots_ids || [])
      } else {
        setName('New Facility')
        setStreetAddress('')
        setCity('')
        setCountry('')
        setFacilityType('other')
        setFacilityTypeOption(FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1])
        setSelectedRobotIds([])
      }
    }
  }, [props.isOpen, props.facilityExisting])

  const isNameValid = /^[a-zA-Z0-9\s\-]+$/.test(name) && /[a-zA-Z0-9]/.test(name)
  const isSaveDisabled = name === '' || !isNameValid

  const onSaveClick = async () => {
    try {
      setSaving(true)
      await props.onSave({
        name,
        facility_type: facilityType,
        street_address: streetAddress || undefined,
        city: city || undefined,
        country: country || undefined,
        robots_ids: selectedRobotIds.length > 0 ? selectedRobotIds : undefined,
      })
    } catch (error) {
      console.log('Error creating facility')
    }
    setSaving(false)
  }

  const onCancelClick = () => {
    setName('New Facility')
    setStreetAddress('')
    setCity('')
    setCountry('')
    setFacilityType('other')
    setFacilityTypeOption(FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1])
    setSelectedRobotIds([])
    props.onClose()
  }

  const onTypeSelected = (e: IOption) => {
    setFacilityTypeOption(e)
    setFacilityType(e.value as string)
  }

  const toggleRobot = (robotId: number) => {
    setSelectedRobotIds(prev => (prev.includes(robotId) ? prev.filter(id => id !== robotId) : [...prev, robotId]))
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.facilityExisting ? 'Edit facility' : 'Add a new facility'}</div>
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description="Facility name"
          value={name}
          placeholder="Input facility name..."
          onChange={e => setName(e.target.value)}
          error="Name must contain letters or numbers"
          hasError={!isNameValid}
        />
        <Dropdown
          className={style.textfield}
          currentOption={facilityTypeOption}
          options={FACILITY_TYPE_OPTIONS}
          onClickOption={onTypeSelected}
          description="Facility type:"
        />
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description="Street address"
          value={streetAddress}
          placeholder="Input street address..."
          onChange={e => setStreetAddress(e.target.value)}
        />
        <TextFieldDescribed
          className={style.textfield}
          description="City"
          value={city}
          placeholder="Input city..."
          onChange={e => setCity(e.target.value)}
        />
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description="Country"
          value={country}
          placeholder="Input country..."
          onChange={e => setCountry(e.target.value)}
        />
      </div>
      <div className={style.row}>
        <div className={style.textfield}>
          <div className={clsx('text-14-400', style.subtitle)}>Robots</div>
          <div className={style.robotList}>
            {props.robots.length === 0 && <div className={style.robotListEmpty}>No robots available</div>}
            {props.robots.map(robot => (
              <label key={robot.id} className={style.robotItem}>
                <input
                  type="checkbox"
                  checked={selectedRobotIds.includes(robot.id)}
                  onChange={() => toggleRobot(robot.id)}
                />
                <span className={style.robotName}>{robot.name || robot.serialNumber}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className={style.footer}>
        <Button.Outlined className={style.btn} disabled={saving} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || isSaveDisabled}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddFacilityModal
